import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, DollarSign, Hash, FileText } from "lucide-react";
import { formatCurrency } from "@/lib/format";
import {
  useRecordManualPaymentV2,
  type PaymentMethodType,
} from "@/api/payments";

interface RecordManualPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: {
    id: string;
    orderNumber: number;
    balanceCents: number;
  };
  onSuccess?: () => void;
}

export function RecordManualPaymentDialog({
  open,
  onOpenChange,
  order,
  onSuccess,
}: RecordManualPaymentDialogProps) {
  const [formData, setFormData] = useState({
    method: "CASH" as PaymentMethodType,
    amountCents: order.balanceCents,
    referenceNumber: "",
    notes: "",
  });

  // Reset form data when order changes or dialog opens
  useEffect(() => {
    if (open && order) {
      setFormData({
        method: "CASH",
        amountCents: order.balanceCents,
        referenceNumber: "",
        notes: "",
      });
    }
  }, [open, order.balanceCents]);

  const recordPayment = useRecordManualPaymentV2(order.id, {
    onSuccess: () => {
      toast.success("Payment Recorded", {
        description: `Successfully recorded ${formatCurrency(formData.amountCents)} payment`,
      });
      onSuccess?.();
      // Reset form
      setFormData({
        method: "CASH",
        amountCents: order.balanceCents,
        referenceNumber: "",
        notes: "",
      });
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error("Failed to Record Payment", {
        description: err.response?.data?.error || "Could not record payment",
      });
    },
  });

  const handleSubmit = () => {
    recordPayment.mutate(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Record Manual Payment</DialogTitle>
          <DialogDescription>
            Record a manual payment for ORD-{order.orderNumber}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="method">Payment Method</Label>
            <Select
              value={formData.method}
              onValueChange={(value) =>
                setFormData({ ...formData, method: value as PaymentMethodType })
              }
            >
              <SelectTrigger id="method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CASH">
                  <div className="flex items-center">
                    <DollarSign className="mr-2 h-4 w-4" />
                    Cash
                  </div>
                </SelectItem>
                <SelectItem value="E_TRANSFER">E-Transfer</SelectItem>
                <SelectItem value="WECHAT">WeChat Pay</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="amount">Amount</Label>
            <CurrencyInput
              id="amount"
              value={formData.amountCents}
              onChange={(value) =>
                setFormData({
                  ...formData,
                  amountCents: value,
                })
              }
            />
            <p className="text-sm text-muted-foreground">
              Outstanding balance: {formatCurrency(order.balanceCents)}
            </p>
          </div>

          {formData.method === "E_TRANSFER" && (
            <div className="space-y-2">
              <Label htmlFor="reference">
                <Hash className="inline mr-1 h-3 w-3" />
                Reference Number
              </Label>
              <Input
                id="reference"
                value={formData.referenceNumber}
                onChange={(e) =>
                  setFormData({ ...formData, referenceNumber: e.target.value })
                }
                placeholder="E-Transfer reference"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="notes">
              <FileText className="inline mr-1 h-3 w-3" />
              Notes (Optional)
            </Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              placeholder="Additional payment details or notes..."
              rows={3}
            />
          </div>

          <div className="rounded-lg bg-muted p-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Recording Payment:</span>
              <span className="text-lg font-bold">
                {formatCurrency(formData.amountCents)}
              </span>
            </div>
            <div className="flex justify-between items-center mt-2">
              <span className="text-sm text-muted-foreground">Method:</span>
              <span className="text-sm font-medium">{formData.method}</span>
            </div>
            {formData.referenceNumber && (
              <div className="flex justify-between items-center mt-2">
                <span className="text-sm text-muted-foreground">
                  Reference:
                </span>
                <span className="text-sm font-medium">
                  {formData.referenceNumber}
                </span>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={recordPayment.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={recordPayment.isPending || formData.amountCents <= 0}
          >
            {recordPayment.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Record Payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
